/**
 * BLUFF CITY FC - Auto Refresh Composable
 * 
 * Provides real-time data updates via polling.
 * Pauses when tab is not visible to save API calls.
 * 
 * Usage:
 *   import { useAutoRefresh } from '@/composables/useAutoRefresh'
 *   
 *   const { 
 *     lastUpdated, 
 *     isRefreshing, 
 *     startAutoRefresh, 
 *     stopAutoRefresh, 
 *     refresh 
 *   } = useAutoRefresh(fetchData, { interval: 30000 })
 *   
 *   onMounted(() => startAutoRefresh())
 *   onUnmounted(() => stopAutoRefresh())
 */

import { ref, onMounted, onUnmounted } from 'vue'

export function useAutoRefresh(fetchFn, options = {}) {
  const {
    interval = 30000,        // Default 30 seconds
    immediate = true,        // Fetch immediately on start
    pauseOnHidden = true     // Pause when tab not visible
  } = options

  const lastUpdated = ref(null)
  const isRefreshing = ref(false)
  const isPaused = ref(false)
  
  let intervalId = null
  let visibilityHandler = null

  // Format last updated time
  const lastUpdatedFormatted = () => {
    if (!lastUpdated.value) return 'Never'
    const now = new Date()
    const diff = Math.floor((now - lastUpdated.value) / 1000)
    
    if (diff < 5) return 'Just now'
    if (diff < 60) return `${diff}s ago`
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`
    return lastUpdated.value.toLocaleTimeString()
  }

  // Perform a refresh
  async function refresh() {
    if (isRefreshing.value) return
    
    isRefreshing.value = true
    try {
      await fetchFn()
      lastUpdated.value = new Date()
    } catch (err) {
      console.error('Auto-refresh error:', err)
    } finally {
      isRefreshing.value = false
    }
  }

  // Start auto-refresh
  function startAutoRefresh() {
    stopAutoRefresh() // Clear any existing
    
    if (immediate) {
      refresh()
    }
    
    intervalId = setInterval(() => {
      if (!isPaused.value) {
        refresh()
      }
    }, interval)

    // Handle visibility changes
    if (pauseOnHidden && typeof document !== 'undefined') {
      visibilityHandler = () => {
        isPaused.value = document.hidden
        // Refresh immediately when tab becomes visible again
        if (!document.hidden && lastUpdated.value) {
          const timeSinceUpdate = Date.now() - lastUpdated.value.getTime()
          if (timeSinceUpdate > interval) {
            refresh()
          }
        }
      }
      document.addEventListener('visibilitychange', visibilityHandler)
    }
  }

  // Stop auto-refresh
  function stopAutoRefresh() {
    if (intervalId) {
      clearInterval(intervalId)
      intervalId = null
    }
    if (visibilityHandler) {
      document.removeEventListener('visibilitychange', visibilityHandler)
      visibilityHandler = null
    }
  }

  // Set refresh interval dynamically
  function setInterval(newInterval) {
    if (intervalId) {
      stopAutoRefresh()
      startAutoRefresh()
    }
  }

  return {
    lastUpdated,
    lastUpdatedFormatted,
    isRefreshing,
    isPaused,
    refresh,
    startAutoRefresh,
    stopAutoRefresh,
    setInterval
  }
}